import { apiGet, apiPost, apiDelete, publicApiGet } from './apiClient';

export interface AcademyAdminRow {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface AcademyAdminLimitInfo {
  currentAdmins: number;
  pendingInvites: number;
  slotsUsed: number;
  maxAdmins: number;
  tier: string;
}

export const academyAdminService = {
  async getInviteStatus(
    academyId: string
  ): Promise<Record<string, 'pending' | 'accepted' | 'expired' | 'revoked'>> {
    const response = await apiGet<Record<string, 'pending' | 'accepted' | 'expired' | 'revoked'>>(
      '/academy-admins/invite-status',
      { params: { academyId } }
    );
    if (response.success) {
      return response.data ?? {};
    }
    throw new Error(response.error ?? 'Failed to fetch invite status');
  },

  async sendInvite(academyId: string, email: string): Promise<{ inviteId: string }> {
    const response = await apiPost<{ inviteId: string }>('/academy-admins/invite', { academyId, email });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to send invite');
  },

  async getInviteDetails(token: string): Promise<{
    academyName: string;
    academyLogoUrl?: string;
    email: string;
    status: string;
  }> {
    const response = await publicApiGet<{
      academyName: string;
      academyLogoUrl?: string;
      email: string;
      status: string;
    }>(`/academy-admins/invite/${token}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Invalid invite link');
  },

  async acceptInvite(token: string): Promise<{ academySlug: string }> {
    const response = await apiPost<{ academySlug: string }>(`/academy-admins/invite/${token}/accept`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to accept invitation');
  },

  async listAdmins(academyId: string): Promise<{
    admins: AcademyAdminRow[];
    limit: AcademyAdminLimitInfo;
  }> {
    const response = await apiGet<{
      admins: AcademyAdminRow[];
      limit: AcademyAdminLimitInfo;
    }>('/academy-admins', { params: { academyId } });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error ?? 'Failed to list administrators');
  },

  async removeAdmin(academyId: string, adminUserId: string): Promise<void> {
    const response = await apiDelete(`/academy-admins/${adminUserId}`, { params: { academyId } });
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to remove administrator');
    }
  },
};
